import {createFeatureSelector, createSelector} from "@ngrx/store";
import {IUserState} from "../reducers/user.reducer";
import {UserMapper} from "../../user/user-mapper";
import {DbStoreState} from "../reducers";
import {UserSubscriptionMapper} from "../../user/user-subscription-mapper";
import {UserSubscription, UserSubscriptionCategory, UserSubscriptionType} from "../../user/user-subscription";
import {UserReferralMapper} from "../../user/user-referral-mapper";

const getDbStoreState = createFeatureSelector<DbStoreState>('dbStore');

export const getUserState = createSelector(getDbStoreState, (dbStoreState: DbStoreState) => dbStoreState.user);

export const getUser = createSelector(getUserState,
  (state: IUserState) => {
    const userSubscriptionData: any[] = state.userSubscription.userSubscription;
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    const userSubscription: UserSubscription = userSubscriptionData ? UserSubscriptionMapper.map(userSubscriptionData, planDescriptionMap) : null;

    return state.userData.userData ? UserMapper.map(state.userData.userData, userSubscription) : null;
  }
);

export const getUserSubscription = createSelector(getUserState,
  (state: IUserState) =>
    state.userSubscription.userSubscription ? UserSubscriptionMapper.map(state.userSubscription.userSubscription, state.planDescriptionMap.planDescriptionMap) : null
);

export const getUserSubscriptionState = createSelector(getUserState,
  (state: IUserState) =>
    state.userData.userData ? UserMapper.parseUserState(UserMapper.mapUserData(state.userData.userData), UserSubscriptionMapper.map(state.userSubscription.userSubscription)) : null
);

export const getUserAvailableSubscriptionsLoaded = createSelector(getUserState,
  (state: IUserState) => state.userAvailableSubscriptions.loaded
);

export const getUserAvailableHostingSubscriptionsLoaded = createSelector(getUserState,
  (state: IUserState) => state.userAvailableSubscriptions.loaded
);


export const getUserMeta = () => createSelector(getUserState,
  (state: IUserState) => {
    const meta: any = state.userAvailableSubscriptions && state.userAvailableSubscriptions.userAvailableSubscriptions && state.userAvailableSubscriptions.userAvailableSubscriptions.meta;

    return meta;
  }
);

export const getUserAvailableSubscriptions = (pricingType: string = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptionDatas: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    const userAvailableSubscriptions: UserSubscription[] = userAvailableSubscriptionDatas
      ? userAvailableSubscriptionDatas.map((userAvailableSubscription: any) => UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap))
      : null;

    return userAvailableSubscriptions;
  }
);


export const getUserReferralSubscription = createSelector(getUserState,
  (state: IUserState) =>
    state.userReferralSubscription.userReferralSubscription ? UserReferralMapper.map(state.userReferralSubscription.userReferralSubscription) : null
);

export const getUserReferralSubscriptionLoaded = createSelector(getUserState,
  (state: IUserState) =>
    state.userReferralSubscription.loaded
);

export const getUserAvailableHostingSubscriptions = (pricingType: string = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptionDatas: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    const userAvailableSubscriptions: UserSubscription[] = userAvailableSubscriptionDatas
      ? userAvailableSubscriptionDatas.map((userAvailableSubscription: any) => UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap))
      : null;

    return userAvailableSubscriptions.filter((subscription: UserSubscription): boolean => {
      return subscription.category !== UserSubscriptionCategory.NONE;
    });
  }
);

export const getUserAvailableSubscription = (subscriptionId: number, fallbackToDefault: boolean = false, pricingType = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptions: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    let userAvailableSubscription: any = userAvailableSubscriptions ? userAvailableSubscriptions.find((subscription: any) => subscription?.plan?.agency_pricing_plan_id === subscriptionId) : null;
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    if (userAvailableSubscriptions && !userAvailableSubscription && fallbackToDefault) {
      userAvailableSubscription = userAvailableSubscriptions.find((subscription: any) => subscription.default);
    }

    return userAvailableSubscription
      ? UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap)
      : null;
  }
);

export const getUserAvailableSubscriptionByProduct = (productId: number, period: 'monthly' | 'yearly', fallbackToDefault: boolean = false, pricingType = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptions: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    let userAvailableSubscription: any = userAvailableSubscriptions ? userAvailableSubscriptions.find((subscription: any) =>
      subscription?.agency_product_id === productId && subscription?.plan?.period === period
    ) : null;

    return userAvailableSubscription
      ? UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap)
      : null;
  }
);

export const getUserUpgradableSubscription = (upgradePeriod: boolean = false, oldUser: boolean = false, currentSubscription: any = null, pricingType = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userSubscriptionData: any[] = currentSubscription ? currentSubscription : state?.userSubscription?.userSubscription;
    const userAvailableSubscriptionsData: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state?.planDescriptionMap?.planDescriptionMap;
    const userSubscription: UserSubscription = userSubscriptionData ? UserSubscriptionMapper.map(userSubscriptionData, planDescriptionMap) : null;
    const userAvailableSubscriptions: UserSubscription[] = userAvailableSubscriptionsData
      ? userAvailableSubscriptionsData.map((userAvailableSubscription: any) => UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap))
      : null;

    let upgradableSubscription: UserSubscription;

    if (oldUser) {
      upgradableSubscription = new UserSubscription();

      userAvailableSubscriptions.map((subscription: UserSubscription) => {
        if (subscription && subscription.plan && subscription.plan.domainCount && userSubscription && userSubscription.plan && userSubscription.plan.domainCount && subscription.plan.domainCount === userSubscription.plan.domainCount && userSubscription.type === subscription.type && subscription.plan.periodNumber > userSubscription.plan.periodNumber) {
          const period = subscription.plan.periodNumber;
          const subsc = {};
          subsc[period] = subscription;
          upgradableSubscription = Object.assign(upgradableSubscription, subsc);
        }
      });
    } else if (userAvailableSubscriptions) {
      upgradableSubscription = userAvailableSubscriptions.find((subscription: UserSubscription): boolean => {
        if (upgradePeriod) {
          if (subscription && subscription.plan && subscription.plan.domainCount && userSubscription && userSubscription.plan && userSubscription.plan.domainCount && subscription.plan.domainCount === userSubscription.plan.domainCount && userSubscription.type === subscription.type && subscription.plan.periodNumber > userSubscription.plan.periodNumber && userSubscription.category === subscription.category) {
            return true;
          }
        } else {
          if (subscription && subscription.plan && subscription.plan.domainCount && userSubscription && userSubscription.plan && subscription.plan.domainCount > userSubscription.plan.domainCount) {
            return true;
          }

          if (userSubscription && userSubscription.type === UserSubscriptionType.FREE && (subscription.type === UserSubscriptionType.ONE_PRODUCT || subscription.type === UserSubscriptionType.PREMIUM)) {
            return true;
          }

          if (userSubscription && userSubscription.type === UserSubscriptionType.ONE_PRODUCT && subscription.type === UserSubscriptionType.PREMIUM) {
            return true;
          }
        }
        return false;
      });
    }

    return upgradableSubscription;
  }
);

export const getUserLoading = createSelector(getUserState,
  (state: IUserState) =>
    state.userData.loading || state.userSubscription.loading || state.userAvailableSubscriptions.loading || state.planDescriptionMap.loading
);

export const getUserSubscriptionLoading = createSelector(getUserState,
  (state: IUserState) =>
    state.userSubscription.loading
);

export const getUserLoaded = createSelector(getUserState,
  (state: IUserState) =>
    state.userData.loaded && state.userSubscription.loaded && state.userAvailableSubscriptions.loaded && state.planDescriptionMap.loaded
);

export const getAvailableFreeTrialSubscriptions = (pricingType: string = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptionDatas: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    const userAvailableSubscriptions: UserSubscription[] = userAvailableSubscriptionDatas
      ? userAvailableSubscriptionDatas.map((userAvailableSubscription: any) => UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap))
      : [];

    return userAvailableSubscriptions.filter((subscription: UserSubscription): boolean => {
      return subscription?.plan?.planPrice === 0 || subscription?.plan?.trialPeriodNumber > 0;
    });
  }
);

export const getAvailablePaidSubscriptions = (pricingType: string = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptionDatas: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    const userAvailableSubscriptions: UserSubscription[] = userAvailableSubscriptionDatas
      ? userAvailableSubscriptionDatas.map((userAvailableSubscription: any) => UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap))
      : [];

    return userAvailableSubscriptions.filter((subscription: UserSubscription): boolean => {
      return subscription?.plan?.planPrice > 0;
    });
  }
);

export const hasPaidActiveSubscription = createSelector(getUserSubscription,
  (subscription) =>
    subscription?.status === 'active' && subscription?.paidClient === true
);

export const hasNotPaidSubscription = (pricingType: string = 'platform') => createSelector(getUserState,
  (state: IUserState) => {
    const userAvailableSubscriptionDatas: any[] = state?.userAvailableSubscriptions?.userAvailableSubscriptions ? state?.userAvailableSubscriptions?.userAvailableSubscriptions[pricingType] : [];
    const planDescriptionMap: any = state.planDescriptionMap.planDescriptionMap;

    const userAvailableSubscriptions: UserSubscription[] = userAvailableSubscriptionDatas
      ? userAvailableSubscriptionDatas.map((userAvailableSubscription: any) => UserSubscriptionMapper.map(userAvailableSubscription, planDescriptionMap))
      : [];

    return userAvailableSubscriptions.some((subscription: UserSubscription): boolean => {
      return subscription?.plan?.planPrice === 0;
    });
  }
);
